<script>
	// game-builder/SoundPicker.svelte
	// 음원(배경음악·효과음) 선택 모달.
	//
	// 저장은 그림과 같이 **assetId(ref)** 로 — 고르면 { assetId, url, name } 을 emit 한다.
	// 서버가 서빙 시점에 current 버전 URL 로 resolve 하므로, 음원을 교체하면 그 id 를 문
	// 모든 슬롯이 자동으로 새 파일을 가리킨다.
	//
	// ── 왜 ImagePicker 를 안 쓰나 ───────────────────────────────────────────────
	// 그 모달은 **썸네일 격자**가 본체다(카드마다 `<img>`, 더블클릭 라이트박스, 타일 크기 조절).
	// 음원에는 보여줄 그림이 없고 대신 **들어봐야** 하므로, 필요한 건 격자가 아니라 재생 버튼이
	// 달린 목록이다. 폴더 트리·버전 기록·일괄 이동은 일부러 뺐다 — 소리는 게임당 몇 개여서
	// 그 장치가 벌이 된다. 필요해지면 ImagePicker 에 `kind` 를 넣어 통합하는 게 다음 수순이다.
	//
	// ⚠️⚠️ `<audio>` 에 **`crossorigin="anonymous"` 가 반드시 있어야 한다.**
	//    없으면 브라우저가 **Origin 없는 응답**(Access-Control-Allow-Origin 이 안 붙은 응답)을
	//    캐시에 넣고, 뒤이은 Phaser 의 XHR(음원 디코드)이 그 캐시를 재사용해 **CORS 로 막힌다.**
	//    그림 `<img>` 에서 실측으로 당한 그 함정과 **같은 것**이다
	//    (SERVER/module/util/gameConfig.js resolveUrlMap 주석 · 빌더 썸네일 11곳).
	import { createEventDispatcher } from "svelte";
	import * as api from "../chat-builder/imageAssetApi.js";
	import { BUILTIN_SOUNDS } from "./gameSlots.js";

	export let open = false;
	/** ⚠️ 파라미터 이름이 chatId 일 뿐 봇도 같은 자산 저장소를 쓴다(그림 피커와 같다). */
	export let chatId;
	/** 업로드가 떨어질 가상 폴더. */
	export let folderPath = "";
	/** 현재 슬롯에 들어 있는 자산 — 목록에서 강조한다. */
	export let currentAssetId = null;

	const dispatch = createEventDispatcher();

	let assets = [];
	let loading = false;
	let busy = "";
	let error = "";
	let search = "";
	let loadedFor = null;
	/** 지금 미리듣는 자산 id. 하나만 울려야 한다 — 둘이 겹치면 무엇을 듣는지 알 수 없다. */
	let playingId = null;
	/** 미리듣기용 Audio 객체 하나를 돌려 쓴다(자산마다 만들면 떠난 소리가 계속 남는다). */
	let audio = null;
	let renamingId = null;
	let renameValue = "";
	let fileInput;
	let dragOver = false;
	/**
	 * 'builtin' = 기본 제공 / 'mine' = 내가 올린 것.
	 *
	 * ⚠️ 기본 제공을 **먼저** 보여준다. 저작자가 처음 열었을 때 빈 목록 대신 바로 고를 수 있는
	 *    것이 있어야 한다 — "음원 파일을 어디서 구하지"에서 막히는 게 가장 흔한 중단 지점이다.
	 *    자기 음원을 이미 올린 사람은 [내가 올린 것] 으로 한 번만 넘기면 된다.
	 */
	let tab = "builtin";

	/** 배경음악 슬롯인지 효과음 슬롯인지 — 기본 제공 목록을 그쪽 것만 보여주는 데 쓴다. */
	export let kindFilter = null; // 'bgm' | 'sfx' | null(전부)

	$: builtinShown = BUILTIN_SOUNDS.filter(
		(b) =>
			(!kindFilter || b.kind === kindFilter) &&
			(search.trim() ? b.label.toLowerCase().includes(search.trim().toLowerCase()) : true),
	);

	$: if (open && loadedFor !== chatId) load();
	$: if (!open) stop();

	$: shown = assets.filter((a) =>
		search.trim() ? (a.name || "").toLowerCase().includes(search.trim().toLowerCase()) : true,
	);

	async function load() {
		loading = true;
		error = "";
		loadedFor = chatId;
		try {
			assets = await api.listSoundAssets(chatId);
		} catch (e) {
			error = e.message || "음원 목록을 불러오지 못했습니다.";
		} finally {
			loading = false;
		}
	}

	/**
	 * 미리듣기.
	 *
	 * ⚠️ 재생 중인 것을 다시 누르면 **멈춘다**(토글). 긴 배경음악을 들어볼 때 끌 방법이 없으면
	 *    모달을 닫아야 조용해진다.
	 */
	/** 올린 음원과 기본 제공이 **같은 재생 경로**를 쓴다(id 와 url 만 다르다). */
	function preview(id, url, label) {
		if (playingId === id) return stop();
		stop();
		const el = new Audio();
		// ⚠️ src 보다 **먼저** 걸어야 한다 — 요청이 나간 뒤에 바꿔도 그 요청에는 안 붙는다.
		el.crossOrigin = "anonymous";
		el.src = url;
		el.onended = () => {
			playingId = null;
		};
		el.onerror = () => {
			playingId = null;
			error = `재생할 수 없는 파일입니다: ${label}`;
		};
		audio = el;
		playingId = id;
		el.play().catch(() => {
			playingId = null;
			error = "브라우저가 재생을 막았습니다. 화면을 한 번 클릭한 뒤 다시 시도해주세요.";
		});
	}

	function stop() {
		if (audio) {
			audio.pause();
			audio.src = "";
			audio = null;
		}
		playingId = null;
	}

	async function upload(files) {
		const list = Array.from(files || []);
		if (list.length === 0) return;
		// 확장자로 먼저 막는다 — 서버·브라우저까지 가서 "재생 안 됨"으로 알게 되면 원인을 모른다.
		const bad = list.find((f) => !api.isAudioFile(f.name));
		if (bad) {
			error = `음원 파일만 올릴 수 있어요(mp3·m4a·ogg·wav): ${bad.name}`;
			return;
		}
		error = "";
		busy = `올리는 중… (0/${list.length})`;
		try {
			// ⚠️ 한 장씩 순서대로 올린다 — 중간에 끊겨도 **올라간 것은 남는다**
			//    (AI 세트 일괄 생성과 같은 이유, assetSetBatch.js 주석).
			for (let i = 0; i < list.length; i++) {
				busy = `올리는 중… (${i}/${list.length})`;
				await api.createSoundAsset(chatId, list[i], folderPath);
			}
			await load();
		} catch (e) {
			error = e.message || "업로드에 실패했습니다.";
		} finally {
			busy = "";
		}
	}

	function pick(asset) {
		stop();
		dispatch("select", { assetId: asset.id, url: asset.url, name: asset.name });
	}

	/**
	 * 기본 제공 고르기 — emit 모양은 올린 음원과 **같다**(assetId 자리에 `builtin:<키>` 가 간다).
	 * 그래서 SoundContext·GameBuilder·서버가 둘을 구분하지 않고 같은 길로 저장한다
	 * (가르는 건 서버 gameConfig.js 의 접두어 분기 한 곳뿐이다).
	 */
	function pickBuiltin(b) {
		stop();
		dispatch("select", { assetId: b.ref, url: b.url, name: b.label });
	}

	async function remove(asset) {
		if (!confirm(`'${asset.name || asset.id}' 을 지울까요?\n이 음원을 쓰던 슬롯은 소리가 없어집니다.`)) return;
		busy = "지우는 중…";
		try {
			await api.deleteAsset(chatId, asset.id);
			await load();
		} catch (e) {
			error = e.message || "삭제에 실패했습니다.";
		} finally {
			busy = "";
		}
	}

	async function commitRename(asset) {
		const name = renameValue.trim();
		renamingId = null;
		if (!name || name === asset.name) return;
		try {
			await api.renameAsset(chatId, asset.id, name);
			assets = assets.map((a) => (a.id === asset.id ? { ...a, name } : a));
		} catch (e) {
			error = e.message || "이름을 바꾸지 못했습니다.";
		}
	}

	const close = () => {
		stop();
		open = false;
	};
</script>

{#if open}
	<div
		class="tw-fixed tw-inset-0 tw-z-[1200] tw-flex tw-items-center tw-justify-center tw-bg-black/50 tw-p-4"
		role="presentation"
		on:click|self={close}
	>
		<div class="tw-flex tw-max-h-[80vh] tw-w-full tw-max-w-[560px] tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-border tw-bg-background tw-shadow-xl">
			<!-- 머리 -->
			<div class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-border tw-px-5 tw-py-3">
				<h2 class="tw-text-base tw-font-semibold tw-text-foreground">소리 고르기</h2>
				<button
					type="button"
					class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-xl tw-leading-none tw-text-muted-foreground"
					on:click={close}
					aria-label="닫기">✕</button
				>
			</div>

			<!-- 기본 제공 / 내가 올린 것 -->
			<div class="tw-flex tw-gap-1 tw-border-b tw-border-border tw-px-5 tw-pt-2">
				{#each [{ v: "builtin", l: "기본 제공" }, { v: "mine", l: "내가 올린 것" }] as t (t.v)}
					<button
						type="button"
						class="tw-cursor-pointer tw-border-0 tw-border-b-2 tw-bg-transparent tw-px-3 tw-py-2 tw-text-sm {tab ===
						t.v
							? 'tw-border-primary tw-font-semibold tw-text-foreground'
							: 'tw-border-transparent tw-text-muted-foreground'}"
						on:click={() => {
							stop();
							tab = t.v;
						}}>{t.l}</button
					>
				{/each}
			</div>

			<!-- 도구 -->
			<div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-b tw-border-border tw-px-5 tw-py-2.5">
				<input
					class="tw-h-9 tw-min-w-0 tw-flex-1 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
					type="search"
					placeholder="이름으로 찾기"
					bind:value={search}
				/>
				{#if tab === "mine"}
					<button
						type="button"
						class="tw-h-9 tw-shrink-0 tw-cursor-pointer tw-rounded-md tw-bg-primary tw-px-3.5 tw-text-sm tw-font-medium tw-text-primary-foreground"
						on:click={() => fileInput?.click()}>음원 올리기</button
					>
				{/if}
				<input
					class="tw-hidden"
					type="file"
					multiple
					accept={api.AUDIO_ACCEPT}
					bind:this={fileInput}
					on:change={(e) => {
						upload(e.target.files);
						e.target.value = "";
					}}
				/>
			</div>

			{#if error}
				<div class="tw-border-b tw-border-destructive/30 tw-bg-destructive/10 tw-px-5 tw-py-2 tw-text-xs tw-text-destructive">
					{error}
				</div>
			{/if}
			{#if busy}
				<div class="tw-border-b tw-border-border tw-bg-muted/50 tw-px-5 tw-py-2 tw-text-xs tw-text-muted-foreground">
					{busy}
				</div>
			{/if}

			<!-- 목록 (드래그 업로드) -->
			<div
				class="tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-3 {dragOver ? 'tw-bg-primary/5' : ''}"
				role="presentation"
				on:dragover|preventDefault={() => (dragOver = tab === "mine")}
				on:dragleave={() => (dragOver = false)}
				on:drop|preventDefault={(e) => {
					dragOver = false;
					// ⚠️ 기본 제공 탭에 파일을 떨어뜨리면 **올리지 않고 탭만 넘긴다.** 거기서 업로드하면
					//    "기본 제공에 내 파일이 들어갔다"로 읽히는데, 실제로는 내 라이브러리에 들어간다.
					if (tab !== "mine") {
						tab = "mine";
						return;
					}
					upload(e.dataTransfer?.files);
				}}
			>
				{#if tab === "builtin"}
					{#if builtinShown.length === 0}
						<p class="tw-py-8 tw-text-center tw-text-sm tw-text-muted-foreground">찾는 이름이 없습니다.</p>
					{:else}
						<ul class="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
							{#each builtinShown as b (b.ref)}
								<li
									class="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-px-3 tw-py-2 {b.ref ===
									currentAssetId
										? 'tw-border-primary tw-bg-primary/5'
										: 'tw-border-border'}"
								>
									<button
										type="button"
										class="tw-h-9 tw-w-9 tw-shrink-0 tw-cursor-pointer tw-rounded-full tw-border tw-border-border tw-bg-muted tw-text-sm tw-text-foreground"
										on:click={() => preview(b.ref, b.url, b.label)}
										title={playingId === b.ref ? "멈춤" : "들어보기"}
										aria-label={playingId === b.ref ? "멈춤" : "들어보기"}
									>
										{playingId === b.ref ? "■" : "▶"}
									</button>
									<div class="tw-min-w-0 tw-flex-1">
										<div class="tw-truncate tw-text-sm tw-text-foreground">
											{b.label}
											{#if b.sub}<span class="tw-text-[11px] tw-text-muted-foreground"> · {b.sub}</span>{/if}
										</div>
										<div class="tw-text-[11px] tw-text-muted-foreground">
											{b.hint}{#if b.ref === currentAssetId}<span class="tw-text-primary"> · 지금 쓰는 소리</span>{/if}
										</div>
									</div>
									<button
										type="button"
										class="tw-h-8 tw-shrink-0 tw-cursor-pointer tw-rounded-md tw-bg-primary tw-px-3 tw-text-xs tw-font-medium tw-text-primary-foreground"
										on:click={() => pickBuiltin(b)}>고르기</button
									>
								</li>
							{/each}
						</ul>
						<!-- 기본 제공은 지우거나 이름을 바꿀 수 없다 — 모든 저작자가 함께 쓰는 것이고,
						     한 사람이 지우면 그 음원을 쓰던 게임이 조용해진다(서버 gameSounds.js 주석). -->
						<p class="tw-mt-3 tw-text-[11px] tw-leading-relaxed tw-text-muted-foreground">
							기본 제공 음원은 모든 게임이 함께 쓰므로 지우거나 이름을 바꿀 수 없습니다. 직접 만든 소리를
							쓰려면 [내가 올린 것] 에서 올리세요.
						</p>
					{/if}
				{:else if loading}
					<p class="tw-py-8 tw-text-center tw-text-sm tw-text-muted-foreground">불러오는 중…</p>
				{:else if shown.length === 0}
					<p class="tw-py-8 tw-text-center tw-text-sm tw-text-muted-foreground">
						{assets.length === 0
							? "아직 올린 음원이 없습니다. 파일을 이 영역에 끌어다 놓거나 [음원 올리기] 를 누르세요."
							: "찾는 이름이 없습니다."}
					</p>
				{:else}
					<ul class="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
						{#each shown as a (a.id)}
							<li
								class="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-px-3 tw-py-2 {a.id ===
								currentAssetId
									? 'tw-border-primary tw-bg-primary/5'
									: 'tw-border-border'}"
							>
								<button
									type="button"
									class="tw-h-9 tw-w-9 tw-shrink-0 tw-cursor-pointer tw-rounded-full tw-border tw-border-border tw-bg-muted tw-text-sm tw-text-foreground"
									on:click={() => preview(a.id, a.url, a.name || a.id)}
									title={playingId === a.id ? "멈춤" : "들어보기"}
									aria-label={playingId === a.id ? "멈춤" : "들어보기"}
								>
									{playingId === a.id ? "■" : "▶"}
								</button>

								<div class="tw-min-w-0 tw-flex-1">
									{#if renamingId === a.id}
										<!-- svelte-ignore a11y-autofocus -->
										<input
											class="tw-h-8 tw-w-full tw-rounded tw-border tw-border-border tw-bg-background tw-px-2 tw-text-sm tw-text-foreground"
											autofocus
											bind:value={renameValue}
											on:blur={() => commitRename(a)}
											on:keydown={(e) => {
												if (e.key === "Enter") commitRename(a);
												if (e.key === "Escape") renamingId = null;
											}}
										/>
									{:else}
										<button
											type="button"
											class="tw-block tw-w-full tw-cursor-text tw-truncate tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-sm tw-text-foreground"
											title="눌러서 이름 바꾸기"
											on:click={() => {
												renamingId = a.id;
												renameValue = a.name || "";
											}}>{a.name || a.id}</button
										>
										{#if a.id === currentAssetId}
											<span class="tw-text-[11px] tw-text-primary">지금 쓰는 소리</span>
										{/if}
									{/if}
								</div>

								<button
									type="button"
									class="tw-h-8 tw-shrink-0 tw-cursor-pointer tw-rounded-md tw-bg-primary tw-px-3 tw-text-xs tw-font-medium tw-text-primary-foreground"
									on:click={() => pick(a)}>고르기</button
								>
								<button
									type="button"
									class="tw-h-8 tw-shrink-0 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-transparent tw-px-2 tw-text-xs tw-text-muted-foreground"
									on:click={() => remove(a)}
									title="지우기">지우기</button
								>
							</li>
						{/each}
					</ul>
				{/if}
			</div>

			<div class="tw-border-t tw-border-border tw-px-5 tw-py-2 tw-text-[11px] tw-leading-relaxed tw-text-muted-foreground">
				mp3 를 권합니다 — 모든 브라우저가 재생할 수 있는 형식입니다. ogg·m4a 는 기기에 따라 소리가 안 날 수 있어요.
			</div>
		</div>
	</div>
{/if}
